import {
  API_CONFIG,
  API_ENDPOINTS,
  getHeadersWithOrg,
  apiCall,
  authFetch,
} from './client';
import {
  ErrorResponse,
  EntityDedupJobResponse,
  EntityDedupCancelResponse,
  EntityDedupCandidatesResponse,
  EntityDedupLatestScan,
  EntityDedupAuditResponse,
  EntityDedupAuditStatus,
  EntityDedupBand,
  EntityDedupPairStatus,
} from '../../types';

/** Human-readable fallback when a dedup trigger hits a busy site (409). */
const BUSY_MESSAGE =
  'A site operation (indexing, delete, or another dedup phase) is already running. Wait for it to finish, then try again.';

/**
 * Thrown when a dedup trigger is rejected with HTTP 409 because the shared
 * SITE_OPERATION guard is held (indexing / delete / another dedup phase).
 * Distinct from a generic failure so the UI can present a "site busy, retry
 * later" message rather than a hard error.
 */
export class DedupBusyError extends Error {
  constructor(message: string = BUSY_MESSAGE) {
    super(message);
    this.name = 'DedupBusyError';
  }
}

/**
 * POST a dedup trigger (analysis or merge) and parse the job handle, mapping a
 * 409 (site busy) onto {@link DedupBusyError}. Both endpoints return 202 with
 * the same EntityDedupJobResponse shape.
 */
async function postDedupTrigger(
  url: string,
  body: Record<string, unknown>,
): Promise<EntityDedupJobResponse> {
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(body),
  });
  if (response.status === 409) {
    const data: ErrorResponse = await response.json().catch(() => ({ error: 'HTTP 409' }));
    const detail = typeof data.detail === 'string' ? data.detail : null;
    throw new DedupBusyError(detail || undefined);
  }
  if (!response.ok) {
    const data: ErrorResponse = await response
      .json()
      .catch(() => ({ error: `HTTP ${response.status}` }));
    const detail = typeof data.detail === 'string' ? data.detail : null;
    throw new Error(detail || data.error || `Request failed: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Job lifecycle — GKG (normal UI -> GKG BFF pattern)
// ---------------------------------------------------------------------------

/** Trigger the analysis (candidate scan) phase. 409 -> DedupBusyError. */
export async function triggerDedupAnalysis(
  siteId: string,
  entityTypes?: string[],
): Promise<EntityDedupJobResponse> {
  return postDedupTrigger(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_ANALYSIS(siteId)}`,
    { entity_types: entityTypes ?? null },
  );
}

/**
 * Trigger the batch-merge phase for the reviewed pairs. 409 -> DedupBusyError.
 * The merge actor is derived server-side (GKG uses the authenticated user), so
 * the client sends no `actor`.
 */
export async function triggerDedupMerge(
  siteId: string,
  pairIds: string[],
): Promise<EntityDedupJobResponse> {
  return postDedupTrigger(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_MERGE(siteId)}`,
    { pair_ids: pairIds },
  );
}

/**
 * Poll a dedup job's lifecycle state. Omit `jobId` to resolve the site's
 * active job (falls back to the most recent). Returns null on 404 (no job yet).
 */
export async function getDedupJobStatus(
  siteId: string,
  jobId?: string,
): Promise<EntityDedupJobResponse | null> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_JOBS(siteId)}`;
  if (jobId) url += `?job_id=${encodeURIComponent(jobId)}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });
  if (response.status === 404) return null;
  if (!response.ok) {
    const data: ErrorResponse = await response
      .json()
      .catch(() => ({ error: `HTTP ${response.status}` }));
    const detail = typeof data.detail === 'string' ? data.detail : null;
    throw new Error(detail || data.error || `Request failed: ${response.status}`);
  }
  return response.json();
}

/** Cancel the site's active dedup job (cooperative or force). */
export async function cancelDedupJob(siteId: string): Promise<EntityDedupCancelResponse> {
  return apiCall<EntityDedupCancelResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_JOBS(siteId)}`,
    { method: 'DELETE', headers: getHeadersWithOrg() },
  );
}

// ---------------------------------------------------------------------------
// Candidate data — flameback-data DIRECT (searchDocuments precedent)
// ---------------------------------------------------------------------------

export interface ListDedupCandidatesOptions {
  band?: EntityDedupBand;
  /** One status, or several — serialized as repeated `status=` params. */
  status?: EntityDedupPairStatus | EntityDedupPairStatus[];
  limit?: number;
  offset?: number;
}

/**
 * List candidate pairs. Calls flameback-data directly (DATA_BASE_URL + Bearer +
 * x-ims-clientid), the same BFF-bypass as {@link searchDocuments}.
 *
 * Pass a `scanId` to review a single completed scan (the per-scan flow). Pass
 * `undefined`/`null` to omit `scan_id` and read the site-wide accumulated set
 * across all scans (the "already reviewed/merged" history). `opts.status`
 * accepts an array to filter by several statuses at once.
 */
export async function listDedupCandidates(
  siteId: string,
  scanId: string | null | undefined,
  opts: ListDedupCandidatesOptions = {},
): Promise<EntityDedupCandidatesResponse> {
  const params = new URLSearchParams();
  if (scanId) params.set('scan_id', scanId);
  if (opts.band) params.set('band', opts.band);
  if (opts.status) {
    const statuses = Array.isArray(opts.status) ? opts.status : [opts.status];
    statuses.forEach((status) => params.append('status', status));
  }
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  return apiCall<EntityDedupCandidatesResponse>(
    `${API_CONFIG.DATA_BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_CANDIDATES(siteId)}?${params.toString()}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/**
 * Resolve the site's most recent ANALYSIS scan (flameback-data DIRECT, same
 * BFF-bypass as {@link listDedupCandidates}). Returns the run's status + counts,
 * or `null` when the site has never been scanned (the endpoint answers HTTP 200
 * with a `null` body). The returned `run_id` == the candidates' `scan_id`, so
 * the Review tab resumes a completed scan across sessions instead of deriving
 * the scan from the ephemeral job history (which a later merge job displaces).
 */
export async function getLatestDedupScan(
  siteId: string,
): Promise<EntityDedupLatestScan | null> {
  return apiCall<EntityDedupLatestScan | null>(
    `${API_CONFIG.DATA_BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_LATEST_SCAN(siteId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

export interface ListAuditOptions {
  /** One actioned status, or several — serialized as repeated `status=` params.
   * Omit to return both MERGED and DISTINCT. */
  status?: EntityDedupAuditStatus | EntityDedupAuditStatus[];
  band?: EntityDedupBand;
  limit?: number;
  offset?: number;
}

/**
 * List the unified entity-dedup audit log (MERGED + DISTINCT decisions with
 * who/when/type/score/band), site-wide across all scans. Reads the append-only
 * records via flameback-data directly, same BFF-bypass as
 * {@link listDedupCandidates}.
 */
export async function listAudit(
  siteId: string,
  opts: ListAuditOptions = {},
): Promise<EntityDedupAuditResponse> {
  const params = new URLSearchParams();
  if (opts.status) {
    const statuses = Array.isArray(opts.status) ? opts.status : [opts.status];
    statuses.forEach((status) => params.append('status', status));
  }
  if (opts.band) params.set('band', opts.band);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const query = params.toString();
  return apiCall<EntityDedupAuditResponse>(
    `${API_CONFIG.DATA_BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_AUDIT(siteId)}${query ? `?${query}` : ''}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/**
 * Mark candidate pairs DISTINCT (writes a symmetric DISTINCT_FROM edge so the
 * pair never resurfaces). Direct data-plane call. Returns the number updated.
 */
export async function recordDedupDecisions(
  siteId: string,
  pairIds: string[],
): Promise<{ updated: number }> {
  return apiCall<{ updated: number }>(
    `${API_CONFIG.DATA_BASE_URL}${API_ENDPOINTS.ENTITY_DEDUP_DECISIONS(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({
        decisions: pairIds.map((pair_id) => ({ pair_id, decision: 'DISTINCT' })),
      }),
    },
  );
}
